import { Inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { API_URL } from '@frontend-site/shared/data';
import { RequestStatusInfo } from '../models/request-status';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { takeUntil } from 'rxjs/operators';
import { interval, startWith, switchMap, tap, Subscription, Subject } from 'rxjs';
import { RequestStatusFetcherService } from './request-status-fetcher.service';

const POLLING_INTERVAL = 3000;

@Injectable()
export class RequestStatusService implements OnDestroy {
    readonly _unsubscribe = new Subject<void>();
    private readonly _fetch = new Subject<void>();
    private timeInterval: Subscription = new Subscription();

    private dataSubject = new BehaviorSubject<RequestStatusInfo[]>(this.data.rows);
    rows$ = this.dataSubject.asObservable();

    constructor(
        private readonly requestStatusFetcherService: RequestStatusFetcherService,
        @Inject(API_URL) private readonly apiUrl: string,
        @Inject(MAT_DIALOG_DATA) private readonly data: { projectId: number; rows: RequestStatusInfo[] }
    ) {
        if (this.gridNeedUpdates(this.data.rows)) {
            this.progressBarUpdate(this.data.projectId);
        }
    }

    private gridNeedUpdates(rows: RequestStatusInfo[]): boolean {
        return rows.some((el) => el.status === 'In Progress' || el.status === 'Not Started');
    }

    private progressBarUpdate(projectId: number): void {
        this.timeInterval = interval(POLLING_INTERVAL)
            .pipe(
                startWith(0),
                tap(() => this._fetch.next()),
                takeUntil(this._unsubscribe)
            )
            .subscribe();

        this._fetch
            .pipe(
                switchMap(() => this.requestStatusFetcherService.getRequestStatus(projectId)),
                tap((res) => this.updateDataForProgressBar(res)),
                takeUntil(this._unsubscribe)
            )
            .subscribe((rows) => {
                this.dataSubject.next(rows);
            });
    }

    updateDataForProgressBar(requests: RequestStatusInfo[]): void {
        if (!this.gridNeedUpdates(requests)) {
            this.timeInterval.unsubscribe();
        }
    }

    ngOnDestroy(): void {
        this._unsubscribe.next();
        this._unsubscribe.complete();
    }
}
